import { useCallback, useRef } from 'react';

import { FabricChannel } from '@atlaskit/analytics-listeners';
import { useAnalyticsEvents } from '@atlaskit/analytics-next';

import type {
	InvitedAppShortcutAllowedSourceAppKey,
	InvitedAppShortcutAllowedTargetAppKey,
} from '../../../common/types';

export const ANALYTICS_CHANNEL = FabricChannel.growth;

export const useInvitedAppAnalytics = () => {
	const { createAnalyticsEvent } = useAnalyticsEvents();

	const isViewedInvitedAppShortcutsFired = useRef<boolean>(false);
	const isFeatureExposedFired = useRef<boolean>(false);

	/**
	 * Fire an analytics event when the user views the invited app shortcuts.
	 */
	const fireViewedInvitedAppShortcuts = useCallback(
		(apps: InvitedAppShortcutAllowedTargetAppKey[]) => {
			if (isViewedInvitedAppShortcutsFired.current) {
				return;
			}

			createAnalyticsEvent({
				eventType: 'screen',
				name: 'invitedAppShortcuts',
				attributes: {
					apps,
				},
			}).fire(ANALYTICS_CHANNEL);

			isViewedInvitedAppShortcutsFired.current = true;
		},
		[createAnalyticsEvent],
	);

	const fireViewedInvitedAppShortcutPopup = useCallback(
		({
			app,
			collaborators,
		}: {
			app: InvitedAppShortcutAllowedTargetAppKey;
			collaborators: number;
		}) => {
			createAnalyticsEvent({
				eventType: 'screen',
				name: 'invitedAppShortcutPopup',
				attributes: {
					app,
					collaborators,
				},
			}).fire(ANALYTICS_CHANNEL);
		},
		[createAnalyticsEvent],
	);

	const fireLinkClickedAcceptInvite = useCallback(
		({ app }: { app: InvitedAppShortcutAllowedTargetAppKey }) => {
			createAnalyticsEvent({
				eventType: 'ui',
				action: 'clicked',
				actionSubject: 'link',
				actionSubjectId: 'acceptInvite',
				attributes: {
					app,
				},
			}).fire(ANALYTICS_CHANNEL);
		},
		[createAnalyticsEvent],
	);

	const fireViewedMoreCollaboratorsTooltip = useCallback(
		({
			app,
			collaborators,
		}: {
			app: InvitedAppShortcutAllowedTargetAppKey;
			collaborators: number;
		}) => {
			createAnalyticsEvent({
				eventType: 'screen',
				name: 'moreCollaboratorsTooltip',
				attributes: {
					app,
					collaborators,
				},
			}).fire(ANALYTICS_CHANNEL);
		},
		[createAnalyticsEvent],
	);

	const fireInvitedAppShortcutsEligibilityChecked = useCallback(
		({ ineligibilityReason }: { ineligibilityReason: string }) => {
			createAnalyticsEvent({
				eventType: 'track',
				action: 'checked',
				actionSubject: 'eligibility',
				attributes: {
					flagKey: 'twcg-460-personalised-invite-notifications-m2',
					ineligibilityReason,
				},
			}).fire(ANALYTICS_CHANNEL);
		},
		[createAnalyticsEvent],
	);

	/**
	 * Fire an analytics event when the feature is exposed.
	 * This event is fired only once per session.
	 * @param isEnabled - the experiment's isEnabled value
	 * @returns void
	 */
	const fireFeatureExposed = useCallback(
		({ isEnabled }: { isEnabled: boolean }) => {
			if (isFeatureExposedFired.current) {
				return;
			}

			createAnalyticsEvent({
				eventType: 'track',
				action: 'exposed',
				actionSubject: 'feature',
				attributes: {
					featureKey: 'twcg-460-personalised-invite-notifications-m2',
					cohort: isEnabled ? 'variant' : 'control',
				},
			}).fire(ANALYTICS_CHANNEL);

			isFeatureExposedFired.current = true;
		},
		[createAnalyticsEvent],
	);

	const fireFetchInvitedAppShortcutsTraitsSucceeded = useCallback(
		({
			sourceApp,
			targetApps,
		}: {
			sourceApp: InvitedAppShortcutAllowedSourceAppKey;
			targetApps: ReadonlyArray<InvitedAppShortcutAllowedTargetAppKey>;
		}) => {
			createAnalyticsEvent({
				eventType: 'operational',
				action: 'succeeded',
				actionSubject: 'fetchInvitedAppShortcutsTraits',
				attributes: {
					sourceApp,
					targetApps,
				},
			}).fire(ANALYTICS_CHANNEL);
		},
		[createAnalyticsEvent],
	);

	const fireFetchInvitedAppShortcutsTraitsFailed = useCallback(
		({
			sourceApp,
			targetApps,
		}: {
			sourceApp: InvitedAppShortcutAllowedSourceAppKey;
			targetApps: ReadonlyArray<InvitedAppShortcutAllowedTargetAppKey>;
		}) => {
			createAnalyticsEvent({
				eventType: 'operational',
				action: 'failed',
				actionSubject: 'fetchInvitedAppShortcutsTraits',
				attributes: {
					sourceApp,
					targetApps,
				},
			}).fire(ANALYTICS_CHANNEL);
		},
		[createAnalyticsEvent],
	);

	const fireFetchCollaboratorsAccessSucceeded = useCallback(
		({
			sourceApp,
			targetApps,
			collaborators,
		}: {
			collaborators: number;
			sourceApp: InvitedAppShortcutAllowedSourceAppKey;
			targetApps: ReadonlyArray<InvitedAppShortcutAllowedTargetAppKey>;
		}) => {
			createAnalyticsEvent({
				eventType: 'operational',
				action: 'succeeded',
				actionSubject: 'fetchCollaboratorsAccess',
				attributes: {
					sourceApp,
					targetApps,
					collaborators,
				},
			}).fire(ANALYTICS_CHANNEL);
		},
		[createAnalyticsEvent],
	);

	const fireFetchCollaboratorsAccessFailed = useCallback(
		({
			sourceApp,
			targetApps,
			error,
		}: {
			error: string;
			sourceApp: InvitedAppShortcutAllowedSourceAppKey;
			targetApps: ReadonlyArray<InvitedAppShortcutAllowedTargetAppKey>;
		}) => {
			createAnalyticsEvent({
				eventType: 'operational',
				action: 'failed',
				actionSubject: 'fetchCollaboratorsAccess',
				attributes: {
					sourceApp,
					targetApps,
					error,
				},
			}).fire(ANALYTICS_CHANNEL);
		},
		[createAnalyticsEvent],
	);

	const fireEligibilityUpdateSucceeded = useCallback(
		({
			sourceApp,
			targetApps,
		}: {
			sourceApp: InvitedAppShortcutAllowedSourceAppKey;
			targetApps: ReadonlyArray<InvitedAppShortcutAllowedTargetAppKey>;
		}) => {
			createAnalyticsEvent({
				eventType: 'operational',
				action: 'succeeded',
				actionSubject: 'eligibilityUpdate',
				attributes: {
					sourceApp,
					targetApps,
				},
			}).fire(ANALYTICS_CHANNEL);
		},
		[createAnalyticsEvent],
	);

	const fireEligibilityUpdateFailed = useCallback(
		({
			sourceApp,
			targetApps,
			error,
		}: {
			error: string;
			sourceApp: InvitedAppShortcutAllowedSourceAppKey;
			targetApps: ReadonlyArray<InvitedAppShortcutAllowedTargetAppKey>;
		}) => {
			createAnalyticsEvent({
				eventType: 'operational',
				action: 'failed',
				actionSubject: 'eligibilityUpdate',
				attributes: {
					sourceApp,
					targetApps,
					error,
				},
			}).fire(ANALYTICS_CHANNEL);
		},
		[createAnalyticsEvent],
	);

	const fireInvitedAppShortcutsContainerRenderFailed = useCallback(
		({
			targetApps,
			error,
			errorStack,
		}: {
			error: string;
			errorStack: string | undefined;
			targetApps: string[];
		}) => {
			createAnalyticsEvent({
				eventType: 'operational',
				action: 'failed',
				actionSubject: 'invitedAppShortcutsContainerRender',
				attributes: {
					targetApps,
					error,
					errorStack,
				},
			}).fire(ANALYTICS_CHANNEL);
		},
		[createAnalyticsEvent],
	);

	return {
		fireViewedInvitedAppShortcuts,
		fireViewedInvitedAppShortcutPopup,
		fireLinkClickedAcceptInvite,
		fireViewedMoreCollaboratorsTooltip,
		fireInvitedAppShortcutsEligibilityChecked,
		fireFeatureExposed,
		fireFetchInvitedAppShortcutsTraitsSucceeded,
		fireFetchInvitedAppShortcutsTraitsFailed,
		fireFetchCollaboratorsAccessSucceeded,
		fireFetchCollaboratorsAccessFailed,
		fireEligibilityUpdateSucceeded,
		fireEligibilityUpdateFailed,
		fireInvitedAppShortcutsContainerRenderFailed,
	};
};
